import { useEffect, useRef, useState, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * usePresence(projectId, token)
 * Connects to wss://<backend>/api/ws/presence/{projectId}?token=<jwt>
 * Returns { roster, count, connected, setPhase }
 */
export const usePresence = (projectId, token) => {
  const [roster, setRoster] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const pingTimer = useRef(null);
  const reconnectTimer = useRef(null);
  const lastPhaseRef = useRef(null);

  useEffect(() => {
    if (!projectId || !token || !API_URL) return undefined;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/ws/presence/${projectId}?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Re-publish phase if known (after reconnect)
        if (lastPhaseRef.current != null) {
          try { ws.send(JSON.stringify({ type: 'phase', phase: lastPhaseRef.current })); } catch { /* noop */ }
        }
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'roster') setRoster(msg.users || []);
        } catch { /* ignore non-JSON */ }
      };

      ws.onclose = () => {
        setConnected(false);
        if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
        if (!cancelled) {
          reconnectTimer.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, [projectId, token]);

  const setPhase = useCallback((phase) => {
    lastPhaseRef.current = phase;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'phase', phase })); } catch { /* noop */ }
    }
  }, []);

  return { roster, count: roster.length, connected, setPhase };
};
